import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api';
import { useCloseTicket, useTicket, useTicketCategories } from '@/lib/queries';
import { formatDate } from '@/lib/utils';

export function TicketDetailPage() {
  const { id = '' } = useParams();
  const ticket = useTicket(id);
  const close = useCloseTicket();
  const cats = useTicketCategories();
  const catName = cats.data?.find((c) => c.id === ticket.data?.categoryId)?.name;

  async function handleClose() {
    if (!confirm('Close this ticket?')) return;
    try {
      await close.mutateAsync({ id, reason: 'closed_by:admin_panel' });
      toast.success('Close command sent to the bot');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  if (ticket.isLoading) return <p className="text-muted-foreground">loading…</p>;
  if (!ticket.data) {
    return (
      <Button variant="ghost" size="sm" asChild>
        <Link to="/tickets">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to list
        </Link>
      </Button>
    );
  }

  const t = ticket.data;
  const customFieldEntries = Object.entries(t.customFieldValues ?? {});

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/tickets">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to list
          </Link>
        </Button>
        {t.status === 'open' && (
          <Button variant="destructive" onClick={handleClose} disabled={close.isPending}>
            <X className="mr-2 h-4 w-4" />
            Close
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="font-mono text-base">{t.id}</span>
            <Badge variant={t.status === 'open' ? 'default' : 'secondary'}>{t.status}</Badge>
          </CardTitle>
          <CardDescription>
            Category: {catName ?? '—'} · Channel: <code className="text-xs">{t.channelId}</code>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Opener</p>
              <p className="font-mono">{t.openerDiscordId}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Claimed by</p>
              <p className="font-mono">{t.claimedByDiscordId ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Opened</p>
              <p>{formatDate(t.openedAt)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Closed</p>
              <p>{t.closedAt ? formatDate(t.closedAt) : '—'}</p>
            </div>
            {t.closeReason && (
              <div className="col-span-2">
                <p className="text-xs text-muted-foreground">Close reason</p>
                <p>{t.closeReason}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {customFieldEntries.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Modal answers</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {customFieldEntries.map(([k, v]) => (
              <div key={k}>
                <p className="text-xs text-muted-foreground">{k}</p>
                <p className="whitespace-pre-wrap text-sm">{String(v)}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Messages ({t.messages.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 max-h-96 overflow-y-auto">
          {t.messages.length === 0 ? (
            <p className="text-sm text-muted-foreground">—</p>
          ) : (
            t.messages.map((m) => (
              <div key={m.id} className="rounded-md border border-border p-2">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="font-mono">{m.authorDiscordId}</span>
                  <span>{formatDate(m.createdAt)}</span>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm">{m.content ?? '(empty)'}</p>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
